import React from 'react';
import clsx from 'clsx';
import { icons } from '/react4xp/components/Svg/Icons/IconList';

interface ButtonProps {
    color?: 'primary' | 'secondary' | 'light';
    hoverColor?: 'primary' | 'secondary' | 'light';
    href?: string;
    target?: boolean;
    type?: 'button';
    underline?: boolean;
    className?: string;
    children?: React.ReactNode; // Make children optional
    onClick?: (event: React.MouseEvent) => void; // Update the type here
    leftIcon?: string; // Name of the left icon
    rightIcon?: string; // Name of the right icon
    socialIcon?: string; // Name of the social media icon
    size?: 'xs' | 'sm' | 'md' | 'lg'; // Size variations
    disabled?: boolean; // Add disabled prop
    menuBtn?: 'pages' | 'topic' | 'sub-topic'; // Menu button variations
    ariaLabel?: string;
    ariaExpanded?: boolean;
    ariaControls?: string;
}

const Button = React.forwardRef<HTMLElement, ButtonProps>(function Button({
    href,
    target,
    type,
    children,
    color,
    size,
    className,
    onClick,
    leftIcon,
    rightIcon,
    socialIcon,
    disabled,
    menuBtn,
    ariaLabel,
    ariaExpanded,
    ariaControls
}: ButtonProps, ref) {

    const Element = href ? 'a' : 'button';

    const elementProps = {
        ref: ref as React.Ref<HTMLAnchorElement & HTMLButtonElement>,
        href: href || null,
        target: target ? '_blank' : undefined,
        type: Element === 'button' ? type : undefined,
        onClick: disabled ? undefined : onClick, // Disable onClick if the button is disabled
        className: clsx(
            'btn',
            `btn--${size}`,
            color === 'primary' && !menuBtn && 'btn--primary', // Only apply color if no menuBtn
            color === 'secondary' && !menuBtn && 'btn--secondary',
            color === 'light' && !menuBtn && 'btn--light',
            disabled && 'disabled',
            menuBtn === 'pages' && 'btn__menu--pages',
            menuBtn === 'topic' && 'btn__menu--topic',
            menuBtn === 'sub-topic' && 'btn__menu--sub-topic',
            className
        ),
        disabled: Element === 'button' ? disabled : undefined // Apply disabled attribute for button element
    };

    let LeftIcon, RightIcon, SocialIcon;
    if (leftIcon) {
        const foundIcon = icons.find(icon => icon.name === leftIcon);
        LeftIcon = foundIcon ? foundIcon.component : null;
    }
    if (rightIcon) {
        const foundIcon = icons.find(icon => icon.name === rightIcon);
        RightIcon = foundIcon ? foundIcon.component : null;
    }
    if (socialIcon) {
        const foundIcon = icons.find(icon => icon.name === socialIcon);
        SocialIcon = foundIcon ? foundIcon.component : null;
    }

    return (
        <Element
            {...elementProps}
            aria-label={ariaLabel}
            aria-expanded={ariaExpanded}
            aria-controls={ariaControls}
        >
            {LeftIcon && <span className="btn__icon btn__icon--left"><LeftIcon /></span>}
            {SocialIcon && <span className="btn__social"><SocialIcon /></span>}
            {children}
            {RightIcon && <span className="btn__icon btn__icon--right"><RightIcon /></span>}
        </Element>
    );
});

export default Button;
